/**
 * @author NikolaV
 */
import { DrawInteractionTool } from './tool-base';
import Feature from 'ol/Feature';
import * as olSphere from 'ol/sphere';
import { Polygon, Polygon as OlPolygon } from 'ol/geom';
import { Overlay } from 'ol';
import { getGeomLength, getPointsDistance } from '@openlayers/ol-utils';
import { getArea, getLocalCoordinatesInCcwOrder } from './tool-utils'
import { transform as olTransform } from 'ol/proj';


export class AreaTool extends DrawInteractionTool {
    onDrawStart(e) { // OlDrawEvent
        const currFeature: Feature = e.feature;
        const [evt] = this.bindEvents(currFeature);
        this.geometryListener = evt;
        document.querySelectorAll('.measure-label').forEach(el => (el as HTMLDivElement).parentElement.style.pointerEvents = 'none')
        super.onDrawStart(e);
    }

    getLabelText(polygon): string {
        let prefix: string = '';
        let area: number;

        const perimeterLength = getGeomLength(polygon, this.mapWrapper.getProjection());
        let perimeterOutput: string = '';

        if (kuura.getProjectProj4Code() === 'EPSG:4326') {
            prefix = '~';
            area = Math.abs(olSphere.getArea(polygon));
        } else {
            const sourceProj = this.mapWrapper.getProjection();
            const ccwLocalCoordinates = getLocalCoordinatesInCcwOrder(polygon, sourceProj);
            area = getArea(ccwLocalCoordinates);
        }

        perimeterOutput += prefix;
        if (perimeterLength > 1000) {
            perimeterOutput += `${Math.round(perimeterLength) / 1000} km`;
        }
        else {
            perimeterOutput += `${Math.round(perimeterLength * 100) / 100} m`;
        }

        if (area > 1000000) {
            return `${prefix}${Math.round(area / 1000000 * 100) / 100} km<sup>2</sup>`;
        }
        return `${prefix}${Math.round(area * 100) / 100} m<sup>2</sup> ${kuura.getMessage('js_perim')}: ${perimeterOutput}`;
    }

    restoreFromCoords(coords: Coordinate[][][]) {
        if (!Array.isArray(coords)) return;

        coords.forEach(measurement => {
            const feature = new Feature({
                geometry: new OlPolygon(measurement)
            });
            feature.set('finished', true);
            this.drawLayer.getSource().addFeature(feature);
            this.bindEvents(feature);
            feature.getGeometry().dispatchEvent('change')
        });
    }

    lastClickCoords = [];
    bindEvents = (feature): [any, Overlay] => {
        const evt = feature.getGeometry().on('change', e => {
            const geom = feature.getGeometry();
            const coords = geom.getCoordinates();

            if (this.lastClickCoords.length !== coords[0].length) {
                this.onDrawProgressCallback(feature);
            }
            this.lastClickCoords = coords[0];

            this.drawSegmentLabels(feature);
        })

        feature.set('type', 'area-measurement');

        return [evt, null];
    }

    drawSegmentLabels= _.throttle((feature: Feature) => {
        if (!feature) return;
        this.clearFeatureLabels(feature);

        const geometry = feature.getGeometry() as Polygon;
        const coordinates = geometry.getCoordinates()[0];

        if (!coordinates || coordinates.length < 2) return;

        // total label
        if (coordinates.length > 3) {
            const totalLabel = this.createLabel(feature);
            totalLabel.getElement().innerHTML = this.getLabelText(geometry);
            totalLabel.setPosition(geometry.getInteriorPoint().getCoordinates());
            this.mapWrapper.getInstance().addOverlay(totalLabel);
        }

        // segment labels
        const sourceProj = this.mapWrapper.getProjection();

        for (let i = 1; i < coordinates.length; i++) {
            const prevCoord = coordinates[i-1];
            const currentCoord = coordinates[i];

            // measurement distance
            const segmentLength = getPointsDistance(
                olTransform(prevCoord, sourceProj, kuura.getProjectProj4Code()),
                olTransform(currentCoord, sourceProj, kuura.getProjectProj4Code())
            );
            let segmentText = this.formatLength(segmentLength);

            const segmentLabel = this.createLabel(feature, true);
            segmentLabel.getElement().innerHTML = segmentText;
            segmentLabel.setPosition([
                (prevCoord[0] + currentCoord[0]) / 2,
                (prevCoord[1] + currentCoord[1]) / 2
            ]);
            this.mapWrapper.getInstance().addOverlay(segmentLabel);
        }
    }, 16);
}

